import { z } from 'zod';

import {
  type AnswerReading,
  answerListLength,
  readAnswerDocument,
  refusedShape,
  unfencedAnswer,
} from './answer-document.ts';
import { type AnswerWire, answerWire } from './answer-wire.ts';
import type { ArtifactStatus } from './artifacts.ts';
import { canonicalJson } from './canonical-json.ts';
import { DomainError } from './errors.ts';
import { studyTypeIdSchema } from './study-types.ts';

/**
 * The queue Goal options are drafted on. The API process stores the request and enqueues it; the
 * worker, which is the only process holding a model, answers it.
 */
export const QUEUE_GOAL_OPTIONS = 'goal.options';

/**
 * How many options a set holds. Three, always: the step asks the researcher to pick one of three,
 * and a set of any other size is not a set the card can show.
 */
export const GOAL_OPTION_COUNT = 3;

/**
 * Placeholder policy values for one option. The minutes are the model's estimate for the whole
 * study, anchored in its instructions to the per-question heuristic the price uses; the text bounds
 * keep each field to about one sentence. Product owns all of them.
 */
export const GOAL_MINUTES_MIN = 3;
export const GOAL_MINUTES_MAX = 30;
export const MAX_GOAL_TITLE_CHARS = 80;
export const MAX_GOAL_DECISION_CHARS = 200;
export const MAX_GOAL_LEARN_CHARS = 300;
export const MAX_GOAL_NOT_LEARN_CHARS = 300;
export const MAX_GOAL_RATIONALE_CHARS = 300;

/** The longest note a reshaped set carries: what the researcher asked for, in a sentence or two. */
export const MAX_GOAL_NOTE_CHARS = 500;

/**
 * How often the Goal step re-reads a pending request, and how long it waits before it offers "Try
 * again". Placeholder policy values, the same pair the Materials step polls on.
 */
export const GOAL_OPTIONS_POLL_MS = 1000;
export const GOAL_OPTIONS_WAIT_MS = 30_000;

const words = (max: number) => z.string().trim().min(1).max(max);

/** One option's shape in the answer format: every key required, and no bound. */
const goalOptionWireShape = z.strictObject({
  title: z.string(),
  decision: z.string(),
  learn: z.string(),
  not_learn: z.string(),
  minutes_per_person: z.int(),
  recommended: z.boolean(),
  rationale: z.string().nullable(),
});

/**
 * The answer format's shape, as the provider is asked to hold it (`GOAL_OPTIONS_ANSWER_WIRE`). It
 * carries no count: the parser holds the set to exactly three options, one Recommended.
 */
export const GOAL_OPTIONS_ANSWER_WIRE_SHAPE = z.strictObject({
  options: z.array(goalOptionWireShape),
});

/** The wire a Goal options draft answers under. */
export const GOAL_OPTIONS_ANSWER_WIRE: AnswerWire = answerWire(
  'goal_options_answer',
  GOAL_OPTIONS_ANSWER_WIRE_SHAPE,
);

/** One option's fields inside their bounds, before the rationale rule. */
const goalOptionDraftFields = goalOptionWireShape.extend({
  title: words(MAX_GOAL_TITLE_CHARS),
  decision: words(MAX_GOAL_DECISION_CHARS),
  learn: words(MAX_GOAL_LEARN_CHARS),
  not_learn: words(MAX_GOAL_NOT_LEARN_CHARS),
  minutes_per_person: z.int().min(GOAL_MINUTES_MIN).max(GOAL_MINUTES_MAX),
  rationale: words(MAX_GOAL_RATIONALE_CHARS).nullable(),
});

/** Whether an option carries a rationale exactly when it is recommended. */
const rationaleMatches = (option: { recommended: boolean; rationale: string | null }): boolean =>
  option.recommended === (option.rationale !== null);

/**
 * One option as the model drafts it, or as the chat agent reshapes it: everything but its id, which
 * the store assigns so no model output ever names an option another set holds.
 *
 * `rationale` is there exactly when `recommended` is true, and `null` otherwise, so an answer
 * cannot carry a reason for an option it did not recommend or recommend one without saying why.
 */
export const goalOptionDraftSchema = goalOptionDraftFields.superRefine((option, context) => {
  if (!rationaleMatches(option)) {
    context.addIssue({
      code: 'custom',
      path: ['rationale'],
      message: option.recommended
        ? 'a recommended option says why it is recommended'
        : 'only the recommended option carries a rationale',
    });
  }
});
export type GoalOptionDraft = z.infer<typeof goalOptionDraftSchema>;

export const goalOptionSchema = goalOptionDraftSchema.safeExtend({ id: z.uuid() });
export type GoalOption = z.infer<typeof goalOptionSchema>;

/**
 * Exactly three options, exactly one of them Recommended. Shared by every shape that holds a list
 * of options, so a draft, a stored set and a chosen Goal are held to the same rule.
 */
const requireOneOfThreeRecommended = (
  options: readonly { recommended: boolean }[],
  context: z.RefinementCtx,
): void => {
  if (options.length !== GOAL_OPTION_COUNT) {
    context.addIssue({
      code: 'custom',
      path: ['options'],
      message: `a set holds exactly ${String(GOAL_OPTION_COUNT)} options`,
    });
  }

  if (options.filter((option) => option.recommended).length !== 1) {
    context.addIssue({
      code: 'custom',
      path: ['options'],
      message: 'exactly one option is Recommended',
    });
  }
};

/** A whole set of drafted options, as the model answers and as the reshape tool takes it. */
export const goalOptionDraftsSchema = z
  .array(goalOptionDraftSchema)
  .superRefine((options, context) => requireOneOfThreeRecommended(options, context));

/**
 * What a set was drafted from: the Study Type and template version the study had, and the version
 * of the confirmed Materials. The step compares these with the study as it stands to decide whether
 * the set it holds still describes the study, and a settle writes nothing onto inputs that moved.
 */
export const goalOptionsInputsSchema = z.strictObject({
  study_type_id: studyTypeIdSchema,
  template_version: z.int().positive(),
  materials_version: z.int().positive(),
});
export type GoalOptionsInputs = z.infer<typeof goalOptionsInputsSchema>;

export const GOAL_OPTION_SET_ORIGINS = ['drafted', 'reshaped'] as const;
export type GoalOptionSetOrigin = (typeof GOAL_OPTION_SET_ORIGINS)[number];

/**
 * One set of three options, with its own id. The id is what "Use this goal" names beside the
 * option, so a tab that loaded an earlier set cannot choose from it once another has replaced it.
 *
 * `drafted` sets come from the one-shot drafter and carry no note; `reshaped` sets come from the
 * chat agent and carry what the researcher asked for.
 */
export const goalOptionSetSchema = z
  .strictObject({
    id: z.uuid(),
    origin: z.enum(GOAL_OPTION_SET_ORIGINS),
    inputs: goalOptionsInputsSchema,
    note: words(MAX_GOAL_NOTE_CHARS).nullable(),
    options: z.array(goalOptionSchema),
  })
  .superRefine((set, context) => {
    requireOneOfThreeRecommended(set.options, context);

    if (new Set(set.options.map((option) => option.id)).size !== set.options.length) {
      context.addIssue({ code: 'custom', path: ['options'], message: 'option ids are unique' });
    }

    if ((set.origin === 'reshaped') !== (set.note !== null)) {
      context.addIssue({
        code: 'custom',
        path: ['note'],
        message:
          set.origin === 'reshaped'
            ? 'a reshaped set says what was asked for'
            : 'a drafted set carries no note',
      });
    }
  });
export type GoalOptionSet = z.infer<typeof goalOptionSetSchema>;

export const GOAL_OPTIONS_REQUEST_STATUSES = [
  'pending',
  'ready',
  'unreadable',
  'unavailable',
] as const;
export type GoalOptionsRequestStatus = (typeof GOAL_OPTIONS_REQUEST_STATUSES)[number];

/**
 * The latest request for a drafted set.
 *
 * - `pending` — the worker has not answered yet.
 * - `ready` — the worker drafted a set, which is the study's current one.
 * - `unreadable` — the model answered, and its answer was not three complete options with one
 *   Recommended.
 * - `unavailable` — no answer could be had: no model is configured, the day's budget is spent, or
 *   the call failed.
 */
export const goalOptionsRequestSchema = z.strictObject({
  id: z.uuid(),
  status: z.enum(GOAL_OPTIONS_REQUEST_STATUSES),
  requested_at: z.iso.datetime(),
  inputs: goalOptionsInputsSchema,
});
export type GoalOptionsRequest = z.infer<typeof goalOptionsRequestSchema>;

export const GOAL_OPTIONS_SCHEMA_VERSION = 1;

/**
 * What `studies.goal_options` holds: the latest request and the current set, each or both absent.
 *
 * Proposals rather than a version history. A set is replaced whenever a new one settles `ready` or
 * the chat agent reshapes it, and nothing downstream moves when it is: only choosing a Goal writes a
 * Goal version, and that version keeps the whole set it was chosen from. A request that fails
 * leaves the previous set where it was.
 */
export const storedGoalOptionsSchema = z.strictObject({
  schema_version: z.literal(GOAL_OPTIONS_SCHEMA_VERSION),
  request: goalOptionsRequestSchema.nullable(),
  set: goalOptionSetSchema.nullable(),
});
export type StoredGoalOptions = z.infer<typeof storedGoalOptionsSchema>;

/**
 * The stored options, or nothing.
 *
 * A shape this build cannot read is treated as no options rather than as an error, like the Study
 * Type suggestion: the options are proposals on one screen, and the study's next request replaces
 * them, so refusing to answer over them would take a study down for the sake of a card.
 */
export const parseStoredGoalOptions = (stored: unknown): StoredGoalOptions | null => {
  if (stored === null || stored === undefined) {
    return null;
  }

  const parsed = storedGoalOptionsSchema.safeParse(stored);

  return parsed.success ? parsed.data : null;
};

/** Whether two sets of inputs describe the same study. */
export const sameGoalOptionsInputs = (left: GoalOptionsInputs, right: GoalOptionsInputs): boolean =>
  left.study_type_id === right.study_type_id &&
  left.template_version === right.template_version &&
  left.materials_version === right.materials_version;

/**
 * Whether a study needs a new request for its current inputs, rather than the one it holds.
 *
 * Not while a set drafted or reshaped from these inputs is on the card, and not while a request for
 * them is pending and younger than `GOAL_OPTIONS_WAIT_MS`. A pending request older than that is one
 * the step has already given up waiting on — its job was lost with a worker, expired, or never ran —
 * and since the queue retries nothing, treating it as still in hand would leave the step waiting on
 * an answer nothing will write.
 */
export const goalOptionsRequestNeeded = ({
  stored,
  inputs,
  now,
}: {
  stored: StoredGoalOptions | null;
  inputs: GoalOptionsInputs;
  now: Date;
}): boolean => {
  const set = stored?.set ?? null;
  if (set !== null && sameGoalOptionsInputs(set.inputs, inputs)) {
    return false;
  }

  const request = stored?.request ?? null;
  const waiting =
    request !== null &&
    request.status === 'pending' &&
    sameGoalOptionsInputs(request.inputs, inputs) &&
    now.getTime() - Date.parse(request.requested_at) < GOAL_OPTIONS_WAIT_MS;

  return !waiting;
};

/** How a request is settled: every status but the one it starts in. */
export type GoalOptionsOutcome =
  | { status: 'ready'; options: readonly GoalOptionDraft[] }
  | { status: 'unreadable' }
  | { status: 'unavailable' };

/**
 * The job the API process sends. It names the request as well as the study, so a job for a request
 * a later one has replaced is recognised as stale and spends nothing.
 */
export const goalOptionsPayloadSchema = z.object({
  studyId: z.uuid(),
  requestId: z.uuid(),
});
export type GoalOptionsPayload = z.infer<typeof goalOptionsPayloadSchema>;

const goalOptionsAnswerSchema = GOAL_OPTIONS_ANSWER_WIRE_SHAPE.extend({
  options: z.array(goalOptionDraftFields),
});

/**
 * The rules a Goal options draft's answer can break, each the reason `parseGoalOptionsAnswer`
 * refuses it:
 *
 * - `document`: not one JSON document;
 * - `shape`: not the answer format's shape, a field outside its bounds included;
 * - `count`: not exactly three options;
 * - `recommended`: not exactly one option Recommended;
 * - `rationale`: an option with a rationale it was not recommended for, or recommended without one.
 */
export const GOAL_OPTIONS_ANSWER_RULES = [
  'document',
  'shape',
  'count',
  'recommended',
  'rationale',
] as const;

export type GoalOptionsAnswerRule = (typeof GOAL_OPTIONS_ANSWER_RULES)[number];

/**
 * The options a model's answer drafts, or the rule it broke when it is not a complete answer.
 *
 * The whole answer must be one JSON document, `{"options": [...]}`, optionally inside one code
 * fence, which is the one wrapping a model adds on its own. Exactly three options, each with every
 * field inside its bounds, exactly one Recommended with a rationale: anything short of that is not
 * an answer, so a partial set can never be stored.
 */
export const parseGoalOptionsAnswer = (
  answer: string,
): AnswerReading<readonly GoalOptionDraft[], GoalOptionsAnswerRule> => {
  const read = readAnswerDocument(unfencedAnswer(answer));
  if (read === null) {
    return { ok: false, rule: 'document' };
  }
  const document = read.value;

  const count = answerListLength(document, 'options');
  if (count !== undefined && count !== GOAL_OPTION_COUNT) {
    return { ok: false, rule: 'count', at: 'options' };
  }
  const parsed = goalOptionsAnswerSchema.safeParse(document);
  if (!parsed.success) {
    return refusedShape(parsed.error);
  }

  const { options } = parsed.data;
  if (options.filter((option) => option.recommended).length !== 1) {
    return { ok: false, rule: 'recommended', at: 'options' };
  }
  const unmatched = options.findIndex((option) => !rationaleMatches(option));
  if (unmatched !== -1) {
    return { ok: false, rule: 'rationale', at: `options.${String(unmatched)}.rationale` };
  }

  return { ok: true, value: options };
};

/**
 * A Goal version: the option that was chosen, and the whole set it was chosen from. Self-contained,
 * so a version shows the chosen Goal, the two alternatives, the Recommended flag and its rationale
 * however many sets have been drafted since.
 */
export const goalContentSchema = z
  .strictObject({
    option_set: goalOptionSetSchema,
    chosen_option_id: z.uuid(),
  })
  .superRefine((content, context) => {
    if (!content.option_set.options.some((option) => option.id === content.chosen_option_id)) {
      context.addIssue({
        code: 'custom',
        path: ['chosen_option_id'],
        message: 'the chosen option is one of the set',
      });
    }
  });
export type GoalContent = z.infer<typeof goalContentSchema>;

/** The chosen option of a Goal version. Non-null by the content schema. */
export const chosenGoalOption = (content: GoalContent): GoalOption =>
  content.option_set.options.find((option) => option.id === content.chosen_option_id) as GoalOption;

/**
 * What "Use this goal" writes, or the refusal that says why it cannot.
 *
 * Decided under the study row lock from what the study holds then, in the order a researcher is told
 * about it: that there is a set to choose from at all, that it is the set they were shown, that it
 * was drafted for the study as it stands now, that the option is in it, that the Goal has not moved
 * since they looked — unless what it holds now is this choice.
 *
 * A set drafted for another Study Type, template or Materials version stays on the card until a new
 * one settles, so the set id alone cannot tell a tab it is choosing a goal for a study that has since
 * changed under it; the inputs can.
 *
 * Whether a change that redrafts the research questions and updates the guide and audience has been
 * acknowledged is the write's to decide, after this, like every other change's (`changeConsequences`).
 */
export const planGoalSelection = ({
  options,
  optionSetId,
  optionId,
  currentInputs,
  current,
  expectedVersion,
}: {
  options: StoredGoalOptions | null;
  optionSetId: string;
  optionId: string;
  /** What the study would draft options from now; see `goalOptionsInputsSchema`. */
  currentInputs: GoalOptionsInputs;
  /** The Goal as it stands, or `null` while none has been chosen. */
  current: { version: number; content: GoalContent } | null;
  /** The version the caller was looking at, `null` for "no Goal yet". */
  expectedVersion: number | null;
}): GoalContent => {
  const set = options?.set ?? null;
  if (set === null) {
    throw new DomainError(
      'goal_options_not_ready',
      'There are no goal options to choose from yet.',
      {},
    );
  }

  if (set.id !== optionSetId) {
    throw new DomainError(
      'goal_options_changed',
      'Your goal options changed; look again before you choose.',
      { option_set_id: optionSetId, current_option_set_id: set.id },
    );
  }

  if (!sameGoalOptionsInputs(set.inputs, currentInputs)) {
    throw new DomainError(
      'goal_options_outdated',
      'These goal options were drafted before the study changed; ask for new ones before you choose.',
      { option_set_id: set.id, set_inputs: set.inputs, current_inputs: currentInputs },
    );
  }

  if (!set.options.some((option) => option.id === optionId)) {
    throw new DomainError('goal_option_unknown', 'That option is not one of these goals.', {
      option_set_id: optionSetId,
      option_id: optionId,
    });
  }

  const next: GoalContent = { option_set: set, chosen_option_id: optionId };
  const changes = current === null || canonicalJson(current.content) !== canonicalJson(next);

  // The choice already stored is not a conflict whichever version the caller read: a retry whose
  // first response was lost finds its own choice current and writes nothing.
  if (changes && (current?.version ?? null) !== expectedVersion) {
    throw new DomainError(
      'artifact_version_not_current',
      'The goal changed while you were choosing; look again before you choose.',
      { requested_version: expectedVersion, current_version: current?.version ?? null },
    );
  }

  return next;
};

/**
 * Why a Goal version exists, from its own content: every version is one the researcher chose, so
 * the reason is what they chose from, or that it replaced the one before it.
 */
export const goalVersionReason = (version: number, content: GoalContent): string => {
  if (version > 1) {
    return `Changed from version ${String(version - 1)}`;
  }

  return content.option_set.origin === 'reshaped'
    ? 'Chosen from options reshaped at your request'
    : 'Chosen from Sigora’s options';
};

/** One line of what the Goal step settled, for the Study so far sheet. */
export const goalSummary = (version: number, content: GoalContent): string =>
  `${chosenGoalOption(content).title}, Version ${String(version)}`;

/**
 * Whether a Goal is chosen and current: approved, at the version it holds now, with nothing
 * outstanding. Every Goal version is written approved, so this is false only once something has
 * staled it.
 */
export const goalChosenCurrent = (state: {
  status: ArtifactStatus;
  currentVersion: number;
  approvedVersion: number | null;
}): boolean => state.status === 'approved' && state.approvedVersion === state.currentVersion;
